import { useMemo, useState } from 'react';
import { useRouteMatch, useTranslation } from '@shared/core';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';

export type NavItemType =
  | {
      type: 'category';
      category: string | StationeryTemplateCategoryEnum;
      title: string;
      isActive: () => boolean;
      to: string;
    }
  | {
      type: 'subcategory';
      category: string | StationeryTemplateCategoryEnum;
      title: string;
      isActive: () => boolean;
      children: Array<{
        category: string;
        title: string;
        to: string;
        isActive: () => boolean;
        thumbnailUrl: string;
      }>;
    };

export type CategoryNavItem = Extract<NavItemType, { type: 'category' }>;
export type SubcategoryNavItem = Extract<NavItemType, { type: 'subcategory' }>;
export type SubcategoryChild = SubcategoryNavItem['children'][number];

const usePreserveQueryParams = () => {
  const { getValueString } = useQueryParamHelper();

  return (path: string, params: string[]) => {
    const searchParams = new URLSearchParams();

    params.forEach(param => {
      const value = getValueString(param);

      if (value) {
        searchParams.set(param, value);
      }
    });

    const cleanPath = path.split('?')[0];

    return `${cleanPath}${Array.from(searchParams.entries()).length ? '?' : ''}${searchParams.toString()}`;
  };
};

export const useNavItems = () => {
  const { dayOfStationeryRoutes, getShopPath } = useCardsRouterContext();
  const isDayOfStationeryRouteMatch = !!useRouteMatch(dayOfStationeryRoutes);
  const getCategoryTitle = useStationeryTemplateCategoryTranslations('dashboardCategoryNav');
  const { t } = useTranslation('stationeryTemplateCategory');

  const [expandedCategoryIndex, setExpandedCategoryIndex] = useState<number>(-1);
  const { currentCategory } = useCardCategoryContext();
  const preserveQueryParams = usePreserveQueryParams();

  const navItems = useMemo<NavItemType[]>(() => {
    const items: NavItemType[] = [
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.saveTheDate,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.saveTheDate),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.saveTheDate,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.saveTheDate), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.invitation,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.invitation),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.invitation,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.invitation), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.enclosure,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.enclosure),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.enclosure,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.enclosure), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.thankYou,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.thankYou),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.thankYou,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.thankYou), ['format']),
      },
      {
        type: 'category',
        category: StationeryTemplateCategoryEnum.holiday,
        title: getCategoryTitle(StationeryTemplateCategoryEnum.holiday),
        isActive: () => currentCategory === StationeryTemplateCategoryEnum.holiday,
        to: preserveQueryParams(getShopPath('wedding', StationeryTemplateCategoryEnum.holiday), ['format']),
      },
      {
        type: 'subcategory',
        category: 'day-of',
        title: t('dashboardCategoryNav', 'dayOf')(),
        isActive: () => isDayOfStationeryRouteMatch,
        children: [
          {
            category: StationeryTemplateCategoryEnum.napkin,
            title: getCategoryTitle(StationeryTemplateCategoryEnum.napkin),
            to: getShopPath('wedding', StationeryTemplateCategoryEnum.napkin),
            isActive: () => currentCategory === StationeryTemplateCategoryEnum.napkin,
            thumbnailUrl: 'https://withjoy.com/media/print/category-dayof-napkin.jpg?v=1',
          },
          {
            category: StationeryTemplateCategoryEnum.menu,
            title: getCategoryTitle(StationeryTemplateCategoryEnum.menu),
            to: getShopPath('wedding', StationeryTemplateCategoryEnum.menu),
            isActive: () => currentCategory === StationeryTemplateCategoryEnum.menu,
            thumbnailUrl: 'https://withjoy.com/media/print/category-dayof-menu.jpg?v=1',
          },
          {
            category: StationeryTemplateCategoryEnum.sign,
            title: getCategoryTitle(StationeryTemplateCategoryEnum.sign),
            to: getShopPath('wedding', StationeryTemplateCategoryEnum.sign),
            isActive: () => currentCategory === StationeryTemplateCategoryEnum.sign,
            thumbnailUrl: 'https://withjoy.com/media/print/category-dayof-sign.jpg?v=1',
          },
        ],
      },
    ];

    return items;
  }, [currentCategory, getCategoryTitle, getShopPath, isDayOfStationeryRouteMatch, preserveQueryParams, t]);

  const onExpandCategoryClick = useEventCallback((idx: number) => {
    setExpandedCategoryIndex(prev => (prev === idx ? -1 : idx));
  });

  return {
    navItems,
    expandedCategoryIndex,
    onExpandCategoryClick,
  };
};
